import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useAuth, type User } from './AuthProvider';
import { AppIcon } from '../theme/icons';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api<{ token: string; user: User }>('/api/auth/login', {
        method: 'POST',
        body: { username, password },
      });
      login(res.token, res.user);
      navigate('/albums', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Benutzer oder Passwort falsch');
      } else {
        setError('Login fehlgeschlagen — versuch es nochmal');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-full flex flex-col justify-center px-6 safe-top safe-bottom relative z-[1]">
      <div className="mb-6">
        <AppIcon size={60} gradId="dkbg-login" dropShadow />
      </div>
      <h1 className="font-sans text-[28px] font-extrabold tracking-[-.02em] text-text leading-tight">
        Doppelklack
      </h1>
      <p className="font-sans text-[14px] text-muted mt-1.5 mb-8">
        Klack. Hab ich. Brauch ich.
      </p>

      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="USERNAME">
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            required
            className="w-full px-4 py-3.5 rounded-[13px] bg-surface border border-border text-text font-sans text-[14px] outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          />
        </Field>

        <Field label="PASSWORT">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            className="w-full px-4 py-3.5 rounded-[13px] bg-surface border border-border text-text font-mono text-[14px] outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          />
        </Field>

        {error && (
          <p className="font-sans text-[13px] text-status-double">{error}</p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full py-[15px] rounded-[14px] bg-primary text-white font-sans font-bold text-[15px] disabled:opacity-50"
        >
          {busy ? 'Logge ein…' : 'Einloggen'}
        </button>
      </form>

      <Link
        to="/register"
        className="block text-center font-sans font-semibold text-[13px] text-accent mt-[18px]"
      >
        Einladungscode einlösen
      </Link>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block font-mono text-[11px] font-semibold tracking-[.06em] text-muted mb-[7px]">
        {label}
      </span>
      {children}
    </label>
  );
}
